import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import defer from 'lodash/defer';
import {
  Button, TranslationProvider, utils,
} from '@resy/ui-kitchen';
import ResyCalendar from '../../../../../../ui-kitchen/src/components/ResyCalendar';
import {
  setSelectedCoreISODate,
  setSelectedCoreSeats,
  setSelectedCoreTime,
} from '../../../../../../ui-kitchen/src/reducers/resyCoreSlice';
import { setSelectedLocationThunk } from '../../../../../../ui-kitchen/src/reducers/resyLocationsSlice';
import { ISO_DATE_FORMAT } from '../../../../../../ui-kitchen/src/constants/resyCore';
import Selector from '../../Nav/Selector';
import LocationsContainer from '../../../containers/LocationsContainer';
import { handleMktAnalytics } from '../AmexUtils';
import { CTA_EVENT_NAME } from '../constants';
import './AmexResySelector.scss';

const AmexResySelector = ({
  $rootScope,
  $routeParams,
  Analytics,
  selectedCoreISODate,
  selectedCoreSeats,
  selectedCoreTime,
  dispatch,
  hideWineLocations,
}) => {
  const { venueInventoryUtils } = utils;

  const [searchFields, setSearchFields] = useState({
    datepicker: {
      model: null,
      config: {
        label: 'Today',
        calendarNumber: {
          default: 1,
          lg: 2,
        },
        calendarLeadDays: null,
      },
    },
  });
  const [dropdowns, setDropdowns] = useState({
    date: false,
    time: false,
    seats: false,
  });
  const [activeMonthMomentObject, setActiveMonthMomentObject] = useState(null);
  const [inventoryByDay, setInventoryByDay] = useState(venueInventoryUtils.getDateRange(moment()));
  const [displayDate, setDisplayDate] = useState(moment(selectedCoreISODate).calendar());
  const [displayTime, setDisplayTime] = useState(selectedCoreTime);
  const [displaySeats, setDisplaySeats] = useState(selectedCoreSeats);
  // only US locations are supported for Amex marketing pages, so filter out any non-US locations
  const locations = useSelector((state) => (state.resyLocations?.nav || state.resyLocations?.web || []).filter((location) => (location.country_code === 'US')));
  const selectedLocation = useSelector((state) => state.resyLocations?.selected || null);
  const isMobileLocations = !$rootScope?.matchMedia?.lg;
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [selectedLocationName, setSelectedLocationName] = useState(
    selectedLocation?.name || locations?.[0]?.name || 'New York'
  );
  const lastSyncedLocationNameRef = useRef(selectedLocation?.name || null);
  const [calendarLeftOffset, setCalendarLeftOffset] = useState(0);
  const selectorsContainerRef = useRef(null);
  const selectorBackdropRef = useRef();

  const titleText = (
    <TranslationProvider translationKey="reservation_date">
      <h3 className="AmexResySelector__calendar--title">Reservation Date</h3>
    </TranslationProvider>
  );

  const updateCalendarPosition = () => {
    const selectorsContainer = selectorsContainerRef.current;
    if (!selectorsContainer) return;

    const dateButton = selectorsContainer.querySelector('#DateSelector__button');
    if (!dateButton) return;

    const containerRect = selectorsContainer.getBoundingClientRect();
    const dateButtonRect = dateButton.getBoundingClientRect();
    setCalendarLeftOffset(Math.max(dateButtonRect.left - containerRect.left, 0));
  };

  const onDropdownClickAmexMarketing = (dropdown) => {
    if (dropdown !== 'location') {
      setIsLocationOpen(false);
    }
    setDropdowns((prevState) => {
      const nextState = {
        date: false,
        time: false,
        seats: false,
      };
      nextState[dropdown] = !prevState[dropdown];
      return nextState;
    });

    if ($rootScope) {
      $rootScope.isBackdropOpen = false;
      $rootScope.$digest?.();
    }
  };

  const closeEverythingAmexMarketing = () => {
    setIsLocationOpen(false);
    setDropdowns({
      date: false,
      time: false,
      seats: false,
    });

    if ($rootScope) {
      $rootScope.isBackdropOpen = false;
      $rootScope.$digest?.();
    }
  };

  const handleFocusAmexMarketing = () => {
    const calendarContainer = document.querySelector('#AmexNav__calendar-dialog');
    const activeDate = document.querySelector('#active-date');

    if (calendarContainer && activeDate) {
      activeDate.focus();
    } else if (calendarContainer) {
      calendarContainer.focus();
    }
  };

  const setMonthValuesAmexMarketing = () => {
    const coreISODate = selectedCoreISODate;

    const activeDate = moment(coreISODate).isBefore(moment(), 'days') ? moment() : moment(coreISODate);

    setActiveMonthMomentObject(activeDate);
    setInventoryByDay(venueInventoryUtils.getDateRange(moment()));

    defer(() => $rootScope?.$digest?.());
  };

  const onDateSelectAmexMarketing = (selectedDate) => {
    dispatch(setSelectedCoreISODate(selectedDate.format(ISO_DATE_FORMAT)));

    const calendarLabel = moment(selectedDate.format(ISO_DATE_FORMAT)).calendar();
    setDisplayDate(calendarLabel);
    setSearchFields((prevState) => ({
      ...prevState,
      datepicker: {
        config: {
          label: calendarLabel,
        },
      },
    }));

    onDropdownClickAmexMarketing('date');

    const extraParams = {
      action_name: 'Date Changed',
    };

    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
  };

  const onPartySizeChange = ({ party_size: quantity }) => {
    if (!selectedCoreSeats) return;

    if (String(selectedCoreSeats) !== quantity) {
      dispatch(setSelectedCoreSeats(quantity));
      setDisplaySeats(quantity);
      closeEverythingAmexMarketing();
      $rootScope?.$evalAsync?.();
    }

    const extraParams = {
      action_name: 'Party Size Changed',
    };

    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
  };

  const onPartySizeClick = () => {
    onDropdownClickAmexMarketing('seats');
    $rootScope?.$evalAsync?.();

    const extraParams = {
      action_name: 'Party Size Clicked',
    };

    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
  };

  const onTimeChange = ({ time }) => {
    if (time === null) return;

    dispatch(setSelectedCoreTime(time));
    if (String(selectedCoreTime) !== time) {
      setDisplayTime(time);
      closeEverythingAmexMarketing();
      $rootScope?.$evalAsync?.();
    }

    const extraParams = {
      action_name: 'Time Changed',
    };

    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
  };

  const onTimeClick = () => {
    onDropdownClickAmexMarketing('time');
    $rootScope?.$evalAsync?.();

    const extraParams = {
      action_name: 'Time Clicked',
    };

    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
  };

  const onLocationToggle = () => {
    setDropdowns({
      date: false,
      time: false,
      seats: false,
    });
    setIsLocationOpen((prevState) => !prevState);
    $rootScope?.$evalAsync?.();

    const extraParams = {
      action_name: 'Location Clicked',
    };

    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
  };

  const onLocationClose = () => {
    setIsLocationOpen(false);
    $rootScope?.$evalAsync?.();
  };

  const onLocationSelect = (location) => {
    if (!location) return;

    setSelectedLocationName(location.name);
    dispatch(setSelectedLocationThunk({ location }));
    onLocationClose();

    const extraParams = {
      action_name: 'Location Changed',
    };

    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
  };

  const toggleCalendar = () => {
    onDropdownClickAmexMarketing('date');
    updateCalendarPosition();
    setTimeout(handleFocusAmexMarketing, 350);

    const extraParams = {
      action_name: 'Calendar Clicked',
    };

    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
  };

  const handleClickOutside = (e) => {
    if (selectorBackdropRef?.current?.contains(e.target)) {
      setIsLocationOpen(false);
    }
  };

  const loadDateAmexMarketing = () => {
    const coreISODate = selectedCoreISODate;

    setSearchFields((prevState) => ({
      ...prevState,
      datepicker: {
        model: moment(coreISODate, ISO_DATE_FORMAT).toDate(),
        config: {
          label: moment(coreISODate, ISO_DATE_FORMAT).calendar(),
        },
      },
    }));

    setDisplayDate(moment(coreISODate).calendar());
    dispatch(setSelectedCoreISODate(moment(coreISODate).format(ISO_DATE_FORMAT)));
    const activeDate = moment(coreISODate).isBefore(moment(), 'days') ? moment() : moment(coreISODate);
    setActiveMonthMomentObject(activeDate);
  };

  const loadPartySizeAmexMarketing = () => {
    setDisplaySeats(selectedCoreSeats);
  };

  const loadTimeAmexMarketing = () => {
    setDisplayTime(selectedCoreTime);
    dispatch(setSelectedCoreTime(selectedCoreTime));
  };

  useEffect(() => {
    loadDateAmexMarketing();
    loadTimeAmexMarketing();
    loadPartySizeAmexMarketing();
    setMonthValuesAmexMarketing();
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMonthValuesAmexMarketing();
    setDisplayDate(moment(selectedCoreISODate).calendar());
  }, [selectedCoreISODate]);

  useEffect(() => {
    setDisplayTime(selectedCoreTime);
  }, [selectedCoreTime]);

  useEffect(() => {
    setDisplaySeats(selectedCoreSeats);
  }, [selectedCoreSeats]);

  useEffect(() => {
    if (selectedLocation?.name && lastSyncedLocationNameRef.current !== selectedLocation.name) {
      lastSyncedLocationNameRef.current = selectedLocation.name;
      setSelectedLocationName(selectedLocation.name);
    }
  }, [selectedLocation]);

  useEffect(() => {
    if ($rootScope) {
      $rootScope.searchProps = {
        onDateSelectAmexMarketing,
        onPartySizeChange,
        onPartySizeClick,
        onTimeChange,
        onTimeClick,
        toggleCalendar,
        dropdowns,
        searchFields,
        selectedSeats: selectedCoreSeats,
        selectedTime: selectedCoreTime,
      };
    }
  }, [
    $rootScope,
    onDateSelectAmexMarketing,
    onPartySizeChange,
    onPartySizeClick,
    onTimeChange,
    onTimeClick,
    toggleCalendar,
    dropdowns,
    searchFields,
    selectedCoreSeats,
    selectedCoreTime,
  ]);

  useEffect(() => {
    if (!dropdowns.date) return undefined;

    updateCalendarPosition();
    window.addEventListener('resize', updateCalendarPosition);
    return () => window.removeEventListener('resize', updateCalendarPosition);
  }, [dropdowns.date]);

  return (
    <div className="AmexResySelector">
      <div className="AmexNav__selectors-container" ref={selectorsContainerRef}>
        <div className="AmexResySelector__location-selector">
          <LocationsContainer
            isMobile={isMobileLocations}
            isOpen={isLocationOpen}
            locations={locations}
            onLocationSelect={onLocationSelect}
            onToggle={isLocationOpen ? onLocationClose : onLocationToggle}
            selectedLocation={selectedLocationName}
            hideWineLocations={hideWineLocations}
          />
        </div>
        {isMobileLocations && isLocationOpen && (
          <Button
            className="AmexResySelector__location-close-button"
            onClick={onLocationClose}
            variant="close"
            aria-label="Close location menu"
          />
        )}
        <Selector
          date={displayDate}
          isMobile={false}
          onPartySizeChange={onPartySizeChange}
          onPartySizeClick={onPartySizeClick}
          onTimeChange={onTimeChange}
          onTimeClick={onTimeClick}
          showTimeFilter
          time={displayTime}
          toggleCalendar={toggleCalendar}
          quantitySelected={displaySeats}
          calendarExpanded={dropdowns.date}
          showCloseButton
        />
        {dropdowns.date && (
          <div
            id="AmexNav__calendar-dialog"
            className={`ResyNav__calendar-container AmexNav__calendar-container ${dropdowns.date ? 'ResyNav__calendar-container--open AmexNav__calendar-container--open' : ''}`}
            aria-hidden={!dropdowns.date}
            role="dialog"
            style={{ '--calendar-left-offset': `${calendarLeftOffset}px` }}
          >
            <div className="ResyCalendar__Dropdown ResyCalendar__Dropdown--global-nav">
              <ResyCalendar
                Analytics={Analytics}
                $routeParams={$routeParams}
                clickableElementIdsToIgnore={['DateSelector__button']}
                numberOfMonths={1}
                getAriaLabel={venueInventoryUtils.getAriaLabel}
                calendarMap={inventoryByDay}
                closeCalendar={toggleCalendar}
                currentMonth={activeMonthMomentObject}
                onClick={onDateSelectAmexMarketing}
                selectedDate={selectedCoreISODate}
                titleText={titleText}
                venueCity="AmexSearch"
              />
            </div>
          </div>
        )}
      </div>
      <div
        className={`AmexResySelector__backdrop ${(dropdowns.date || isLocationOpen) ? 'AmexResySelector__backdrop--open' : ''}`}
        ref={selectorBackdropRef}
      />
    </div>
  );
};

AmexResySelector.propTypes = {
  $rootScope: PropTypes.shape({
    $digest: PropTypes.func,
    $evalAsync: PropTypes.func,
    isBackdropOpen: PropTypes.bool,
    matchMedia: PropTypes.shape({
      lg: PropTypes.bool,
    }),
    searchProps: PropTypes.shape({}),
  }),
  $routeParams: PropTypes.shape({}).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  selectedCoreISODate: PropTypes.string.isRequired,
  selectedCoreSeats: PropTypes.number.isRequired,
  selectedCoreTime: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  hideWineLocations: PropTypes.bool,
};

AmexResySelector.defaultProps = {
  $rootScope: null,
  hideWineLocations: false,
};

export default AmexResySelector;
